import { z } from 'zod';

const uuid = z.string().uuid();

/**
 * A Claude model MentorIA can run on, with Bedrock prices per million tokens and what its
 * request may carry (the 5 series rejects sampling parameters).
 */
export type MentoriaModelSpec = {
  key: 'haiku-4-5' | 'sonnet-4-6' | 'sonnet-5' | 'opus-5';
  label: string;
  modelId: string;
  inputPerMillion: number;
  outputPerMillion: number;
  request: { temperature?: number; thinking?: 'disabled' };
};

/**
 * Why MentorIA refuses to open a session or answer a turn (ADR-0009, ADR-0010): the API says
 * which gate closed so the app can show the family what to do.
 */
export const mentoriaRefusalReasonSchema = z.enum([
  'not-enabled',
  'no-consent',
  'no-period',
  'period-unpaid',
  'quota-exhausted',
]);

/**
 * A refusal reason value.
 */
export type MentoriaRefusalReason = z.infer<typeof mentoriaRefusalReasonSchema>;

/**
 * The learning-profile questionnaire a student answers at first use: how they like to be
 * explained things, how much help they want, their pace, and two short free-text answers. The
 * free text goes into the prompt, so it is screened for personal data before it is stored.
 */
export const mentoriaProfileInputSchema = z.object({
  explanationStyle: z.enum(['ejemplos', 'pasos', 'dibujos', 'historias']),
  helpStyle: z.enum(['pistas', 'paso-a-paso']),
  pace: z.enum(['despacio', 'normal', 'rapido']),
  interests: z.string().trim().max(160).default(''),
  hardest: z.string().trim().max(160).default(''),
});

/**
 * Body of `PUT /students/{id}/mentoria/profile`.
 */
export type MentoriaProfileInput = z.input<typeof mentoriaProfileInputSchema>;

/**
 * A stored learning profile.
 */
export type MentoriaProfile = z.output<typeof mentoriaProfileInputSchema> & { updatedAt: string };

/**
 * The longest period a guardian activates at once.
 */
export const MENTORIA_ACTIVATION_MAX_MONTHS = 12;

/**
 * Body of `POST /students/{id}/mentoria/activations`: how many months, from today or from the
 * day after the current period ends.
 */
export const activateMentoriaInputSchema = z.object({
  months: z.number().int().min(1).max(MENTORIA_ACTIVATION_MAX_MONTHS).default(1),
});

/**
 * Input of the activation route.
 */
export type ActivateMentoriaInput = z.input<typeof activateMentoriaInputSchema>;

/**
 * Body of `POST /mentoria/sessions`: the student, the subject and the topic (by plan id or by
 * title). `modelId` is honored only for demo sessions opened with the `mentoria:use` key.
 */
export const openMentoriaSessionInputSchema = z
  .object({
    studentUserId: uuid,
    subjectId: uuid,
    topic: z.string().trim().min(3).max(200).optional(),
    topicId: uuid.optional(),
    modelId: z.string().trim().max(120).optional(),
  })
  .refine((value) => value.topic || value.topicId, {
    message: 'A topic (topicId or topic) is required',
    path: ['topic'],
  });

/**
 * Input of the session route.
 */
export type OpenMentoriaSessionInput = z.input<typeof openMentoriaSessionInputSchema>;

/**
 * Query of `GET /mentoria/topics`: the subject, and the grade for callers with the key (a
 * student's grade comes from their enrollment).
 */
export const mentoriaTopicsQuerySchema = z.object({
  gradeId: uuid.optional(),
  subjectId: uuid,
});

/**
 * Query of `GET /staff/tenants/{id}/mentoria-usage`: the month to invoice (`YYYY-MM`, the
 * current one by default).
 */
export const mentoriaUsageQuerySchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM')
    .optional(),
});

/**
 * A topic MentorIA can ground a session on: from the school's plan (with id, logros and DBA
 * reference) or from the curated fallback (no id). `current` marks the period's topics and the
 * ones the class log says were covered lately.
 */
export type MentoriaTopicView = {
  id: string | null;
  topic: string;
  logro: string | null;
  logros: string[];
  dbaReference: string | null;
  periodPosition: number | null;
  current: boolean;
  coveredOn: string | null;
};

/**
 * A paid period of MentorIA for one student, with the ledger charge that pays it.
 */
export type MentoriaActivationView = {
  id: string;
  studentUserId: string;
  startsOn: string;
  endsOn: string;
  months: number;
  price: number;
  currency: string;
  status: 'active' | 'cancelled';
  paid: boolean;
  charge: { id: string; status: string; balance: number; dueOn: string } | null;
  createdAt: string;
};

/**
 * The student's monthly quota and what is left of it.
 */
export type MentoriaQuotaView = {
  month: string;
  monthly: number;
  used: number;
  remaining: number;
};

/**
 * One past session as the family sees it in the list: what was studied and the memory MentorIA
 * kept of it (a summary, never the transcript).
 */
export type MentoriaSessionSummaryView = {
  id: string;
  subjectName: string;
  topic: string;
  createdAt: string;
  messages: number;
  summary: string | null;
};

/**
 * What `GET /students/{id}/mentoria` returns: every gate's state, the current period, the
 * quota, the profile, the subjects with topics and the past sessions.
 */
export type StudentMentoriaView = {
  student: { id: string; givenName: string | null; gradeName: string | null };
  enabled: boolean;
  familyPrice: number | null;
  currency: string;
  consent: { active: boolean; id: string | null; grantedAt: string | null };
  activation: MentoriaActivationView | null;
  active: boolean;
  reason: MentoriaRefusalReason | null;
  quota: MentoriaQuotaView;
  profile: MentoriaProfile | null;
  subjects: { subjectId: string; subjectName: string; topics: number }[];
  sessions: MentoriaSessionSummaryView[];
};

/**
 * What ClassGO invoices a school for a month (ADR-0009): the students with a paid period in
 * the month and what each spent.
 */
export type MentoriaTenantUsage = {
  month: string;
  modelId: string;
  monthlyQuota: number;
  activeStudents: number;
  students: {
    studentUserId: string;
    name: string;
    activated: boolean;
    messages: number;
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
  }[];
  totals: { messages: number; inputTokens: number; outputTokens: number; costUsd: number };
};
